import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Plus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/app/PageHeader";
import { PlanForm, planPayload, StatusBadge, STATUS } from "../components/app/PlanForm";

const FLOW = ["proposed", "under_review", "approved", "in_progress", "completed", "evaluated", "deferred", "cancelled"];

const NewPlan = ({ open, onClose, priorities, initial }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [gkks, setGkks] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ priority_id: initial || "", indicators: [] });
  useEffect(() => {
    if (!open) return;
    api.get("/gkks").then((r) => setGkks(r.data));
    api.get("/users/assignable").then((r) => setUsers(r.data));
    const p = priorities.find((x) => x.priority_id === initial);
    setForm({ priority_id: initial || "", gkk_id: p?.gkk_id || (user.role === "gkk_president" ? user.gkk_id : null), indicators: [] });
  }, [open, initial, priorities, user]);
  const save = async () => {
    try { const r = await api.post("/plans", { priority_id: form.priority_id, ...planPayload(form) }); toast.success("Action plan proposed"); nav(`/plans/${r.data.plan_id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New action plan</DialogTitle><DialogDescription>Every plan answers an approved pastoral priority. It starts as Proposed.</DialogDescription></DialogHeader>
        <Select value={form.priority_id || "none"} onValueChange={(v) => setForm({ ...form, priority_id: v })}>
          <SelectTrigger className="bg-white" data-testid="plan-priority-select"><SelectValue placeholder="Approved priority" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="none" disabled>Choose an approved priority</SelectItem>{priorities.map((p) => <SelectItem key={p.priority_id} value={p.priority_id}>{p.title} · {p.gkk_name}</SelectItem>)}</SelectContent>
        </Select>
        <PlanForm form={form} setForm={setForm} gkks={gkks} users={users} lockGkk={user.role === "gkk_president"} />
        <DialogFooter><Button onClick={save} disabled={!form.priority_id || form.priority_id === "none"} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="plan-save-button">Submit plan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Plans() {
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [status, setStatus] = useState("");
  const newFor = params.get("new");
  const [open, setOpen] = useState(!!newFor);
  useEffect(() => { api.get("/plans", { params: status ? { status } : {} }).then((r) => setPlans(r.data)); }, [status]);
  useEffect(() => { api.get("/priorities").then((r) => setPriorities(r.data.filter((p) => p.status === "approved"))); }, []);
  const counts = plans.reduce((a, p) => ({ ...a, [p.status]: (a[p.status] || 0) + 1 }), {});

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Survey-to-Action planner" title="Pastoral Action Plans" testId="plans-header">
        Proposed → Under Review → Approved → In Progress → Completed → Evaluated. Only the Parish Priest approves plans, sets approved budgets, defers or cancels.
      </PageHeader>
      <div className="flex flex-wrap items-center gap-2" data-testid="status-filter">
        <button onClick={() => setStatus("")} className={`rounded-full px-3 py-1 text-sm ${!status ? "bg-amber-900 text-amber-50" : "bg-[#F5F0E6] text-stone-700"}`} data-testid="status-filter-all">All</button>
        {FLOW.map((s) => <button key={s} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 text-sm ${status === s ? "bg-amber-900 text-amber-50" : "bg-[#F5F0E6] text-stone-700"}`} data-testid={`status-filter-${s}`}>{STATUS[s][0]}{!status && counts[s] ? ` (${counts[s]})` : ""}</button>)}
        <Button onClick={() => setOpen(true)} className="ml-auto bg-amber-900 hover:bg-[#5B210B]" data-testid="new-plan-button"><Plus className="h-4 w-4 mr-2" />New action plan</Button>
      </div>
      <div className="space-y-3" data-testid="plans-list">
        {plans.map((p) => (
          <Link key={p.plan_id} to={`/plans/${p.plan_id}`} className="flex items-center gap-4 rounded-xl border border-amber-700/15 bg-white p-5 hover:border-amber-700/40 transition-colors" data-testid={`plan-row-${p.plan_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-amber-800/80">{p.concern} · {p.gkk_name}</p>
              <p className="mt-1 font-serif-display text-lg font-semibold text-amber-950 truncate">{p.intervention}</p>
              <p className="text-xs text-stone-500">Coordinator: {p.coordinator_name || "not assigned"} · {p.start_date || "—"} → {p.target_date || "—"}</p>
            </div>
            <StatusBadge s={p.status} testId={`plan-status-${p.plan_id}`} />
            <ChevronRight className="h-4 w-4 text-stone-400" />
          </Link>
        ))}
        {!plans.length && <p className="text-sm text-stone-500">No action plans here yet. Plans are created from approved priorities.</p>}
      </div>
      <NewPlan open={open} onClose={() => { setOpen(false); setParams({}); }} priorities={priorities} initial={newFor} />
    </div>
  );
}
